/**
 * Affichage du suivi transaction (objet `tracking` renvoyé par l’API ou calculé côté client).
 */
import { DEFAULT_SELLER_PAYOUT_HOURS, SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { buyerPrivateUnlocked, isLitigeRowOpen } from "@/lib/buyerPrivateAccess";

export { DEFAULT_SELLER_PAYOUT_HOURS, SELLER_PAYOUT_HOURS };

function hoursUntilIso(iso, nowMs = Date.now()) {
  const ms = iso ? Date.parse(String(iso)) : NaN;
  if (!Number.isFinite(ms)) return null;
  const diff = ms - nowMs;
  if (diff <= 0) return 0;
  return Math.ceil(diff / 3600000);
}

function formatHoursFr(h) {
  if (h == null) return null;
  if (h <= 0) return "maintenant";
  if (h < 24) return `dans ${h} h`;
  const d = Math.floor(h / 24);
  const r = h % 24;
  if (r === 0) return `dans ${d} j`;
  return `dans ${d} j ${r} h`;
}

function disputeAnchorMs(tx) {
  const raw = tx?.capture_confirmed_at;
  if (!raw) return null;
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : null;
}

/** Recalcule le tracking si l’API ne l’a pas fourni (fallback Supabase direct). */
export function buildTransactionTrackingClient(tx, { litiges = [], role = "buyer", hours = SELLER_PAYOUT_HOURS } = {}) {
  const nowMs = Date.now();
  const statut = String(tx?.statut || "").toLowerCase();
  const openLitige = (litiges || []).find(isLitigeRowOpen) || null;
  const unlocked = role === "buyer" ? buyerPrivateUnlocked(tx, litiges) : null;

  const payoutAtMs = tx?.seller_payout_available_at
    ? Date.parse(String(tx.seller_payout_available_at))
    : NaN;
  const anchorMs = disputeAnchorMs(tx);
  const disputeEndMs = anchorMs != null && hours > 0 ? anchorMs + hours * 3600000 : null;

  const payout_due =
    statut === "fonds_debloques" &&
    !tx?.stripe_connect_transfer_id &&
    !tx?.seller_payout_on_hold &&
    Number.isFinite(payoutAtMs) &&
    payoutAtMs <= nowMs;

  const attention_reasons = [];
  if (openLitige) attention_reasons.push("Litige ouvert — traiter en priorité");
  if (tx?.seller_payout_on_hold) attention_reasons.push("Versement vendeur suspendu");
  if (payout_due) attention_reasons.push("Versement vendeur éligible");
  if (tx?.seller_payout_failed_at) attention_reasons.push("Échec versement Stripe");
  if (tx?.stripe_charge_dispute_id) attention_reasons.push("Chargeback Stripe");
  if (statut === "appel_effectue") attention_reasons.push("Décision acheteur requise");

  let money_location_label = "—";
  if (statut === "en_attente") money_location_label = "Paiement non finalisé";
  else if (statut === "rembourse") money_location_label = "Remboursé";
  else if (tx?.stripe_connect_transfer_id) money_location_label = "Versé au vendeur";
  else if (statut === "fonds_debloques") {
    if (tx?.seller_payout_on_hold || openLitige) {
      money_location_label = "Versement suspendu (litige)";
    } else if (Number.isFinite(payoutAtMs) && payoutAtMs > nowMs) {
      money_location_label = `Séquestre · délai ${hours} h`;
    } else if (payout_due) {
      money_location_label = "Versement vendeur en file";
    } else {
      money_location_label = "RDV confirmé";
    }
  } else if (["paye", "appel_en_cours", "appel_effectue", "litige"].includes(statut)) {
    money_location_label = openLitige || statut === "litige" ? "Séquestre · litige" : "Séquestre plateforme";
  }

  const buyer_access = unlocked
    ? { code: "unlocked", label: "Coordonnées débloquées" }
    : openLitige
      ? { code: "blocked_litige", label: "Coordonnées masquées — litige" }
      : statut === "rembourse"
        ? { code: "blocked_refund", label: "Coordonnées masquées — remboursement" }
        : { code: "locked", label: "Coordonnées masquées jusqu’à validation du RDV" };

  const pipeline_buyer = {
    en_attente: "Paiement",
    paye: "Appel à planifier",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Décision RDV",
    fonds_debloques: "RDV validé",
    litige: "Litige",
    rembourse: "Remboursé",
    annule: "Annulé",
    echec_paiement: "Échec paiement",
  };

  const pipeline_seller = {
    en_attente: "Attente paiement",
    paye: "Paiement reçu",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Décision acheteur",
    fonds_debloques: tx?.seller_payout_on_hold || openLitige ? "Versement bloqué" : "Versement programmé",
    litige: "Litige",
    rembourse: "Remboursé",
    annule: "Annulé",
    echec_paiement: "Échec",
  };

  const pipeline_label =
    role === "seller" ? pipeline_seller[statut] || statut : pipeline_buyer[statut] || statut;

  return {
    money_location_label,
    pipeline_label,
    list_hint: money_location_label,
    buyer_access: buyer_access.code,
    buyer_access_label: buyer_access.label,
    payout_due,
    can_admin_release_payout: Boolean(payout_due && !tx?.seller_payout_failed_at),
    needs_attention: attention_reasons.length > 0,
    attention_reasons,
    hours_until_payout: tx?.seller_payout_available_at
      ? hoursUntilIso(tx.seller_payout_available_at, nowMs)
      : null,
    hours_until_dispute_end: disputeEndMs
      ? hoursUntilIso(new Date(disputeEndMs).toISOString(), nowMs)
      : null,
    dispute_window_open: disputeWindowOpen,
    dispute_window_ends_at: disputeEndMs ? new Date(disputeEndMs).toISOString() : null,
    seller_payout_available_at: tx?.seller_payout_available_at || null,
    capture_confirmed_at: tx?.capture_confirmed_at || null,
    payout_hours: hours,
    next_step_eta:
      role === "seller" && tx?.seller_payout_available_at
        ? formatHoursFr(hoursUntilIso(tx.seller_payout_available_at, nowMs))
        : null,
    urgency: openLitige || payout_due ? "high" : "normal",
  };
}

export function resolveTransactionTracking(tx, { litiges = [], role = "buyer" } = {}) {
  if (tx?.tracking && typeof tx.tracking === "object") return tx.tracking;
  return buildTransactionTrackingClient(tx, { litiges, role });
}

export function trackingListSubline(tracking, role) {
  if (!tracking) return "";
  const parts = [tracking.pipeline_label, tracking.money_location_label].filter(Boolean);
  if (role === "buyer" && tracking.buyer_access === "locked") parts.push("coordonnées masquées");
  if (tracking.hours_until_payout != null && tracking.hours_until_payout > 0) {
    parts.push(formatHoursFr(tracking.hours_until_payout));
  }
  return parts.filter((p, i, arr) => arr.indexOf(p) === i).join(" · ");
}

export function formatDtFr(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
}
